<template>
  <v-card>
    <v-toolbar>
      <v-toolbar-title>{{ $i18n.translate("Serial Number") }} - {{ form.productSerialNumberKey }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-tooltip bottom v-if="canUpdate && isEditing">
        <template v-slot:activator="{ on, attrs }">
          <v-btn class="mr-2" fab small @click="onSubmit" v-bind="attrs" :disabled="!valid" v-on="on">
            <v-icon>mdi-content-save</v-icon>
          </v-btn>
        </template>
        <span>{{ $i18n.translate("Save") }}</span>
      </v-tooltip>
      <v-tooltip bottom v-if="canUpdate && !isEditing && !form.effective">
        <template v-slot:activator="{ on, attrs }">
          <v-btn class="mr-2" fab small @click="reactivateDialog = true" v-bind="attrs" v-on="on">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </template>
        <span>{{ $i18n.translate("Reactivate") }}</span>
      </v-tooltip>
      <v-tooltip bottom v-if="canUpdate">
        <template v-slot:activator="{ on, attrs }">
          <v-btn class="mr-2" fab small @click="onEdit" v-bind="attrs" v-on="on">
            <v-icon v-if="isEditing">mdi-close</v-icon>
            <v-icon v-else>mdi-pencil</v-icon>
          </v-btn>
        </template>
        <span v-if="isEditing">{{ $i18n.translate("Cancel") }}</span>
        <span v-else>{{ $i18n.translate("Edit") }}</span>
      </v-tooltip>
      <template v-slot:extension>
        <v-tabs v-model="tab">
          <v-tabs-slider color="info"></v-tabs-slider>
          <v-tab>{{ $i18n.translate("Details") }}</v-tab>
          <v-tab>{{ $i18n.translate("Relations") }}</v-tab>
          <v-tab>{{ $i18n.translate("Custom Fields") }}</v-tab>
          <v-tab>{{ $i18n.translate("Timeline") }}</v-tab>
        </v-tabs>
      </template>
    </v-toolbar>
    <v-card-text>
      <ApiError :errors="errors"></ApiError>
      <div class="text-center" v-if="isBusy">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>

      <v-form :value="valid" @submit.prevent="onSubmit" v-if="!isBusy" v-model="valid">
        <v-tabs-items v-model="tab">
          <v-tab-item>
            <v-container>
              <v-row>
                <v-col cols="6">
                  <v-text-field
                    name="productSerialNumberKey"
                    :label="$i18n.translate('Serial Number')"
                    id="productSerialNumberKey"
                    class="required"
                    v-model="form.productSerialNumberKey"
                    :rules="rules.productSerialNumberKey"
                    :disabled="!isEditing"
                    :error-messages="$error.getValidationError(errors, 'productSerialNumberKey')"
                    @input="$error.clearValidationError(errors, 'productSerialNumberKey')"
                  ></v-text-field>
                </v-col>

                <v-col cols="6">
                  <v-text-field
                    id="product"
                    name="product"
                    :label="$i18n.translate('Product')"
                    :value="product ? product.productKey : ''"
                    readonly
                  >
                    <template v-slot:append>
                      <v-tooltip bottom v-if="product && $privilege.hasPrivilege('PRODUCT_READ')">
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            icon
                            small
                            :to="{ name: 'product', params: { id: product.id } }"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon>mdi-open-in-new</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $i18n.translate("View Product") }}</span>
                      </v-tooltip>
                    </template>
                  </v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="6">
                  <v-checkbox
                    class="ml-2"
                    :input-value="form.participantProvided"
                    :label="$i18n.translate('Provided by the Participant')"
                    disabled
                    hide-details
                  ></v-checkbox>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="6">
                  <DateTimePickerField
                    v-model="form.effectiveDate"
                    :disabled="!isEditing"
                    :label="$i18n.translate('Effective Date')"
                    :rules="rules.getDateRules(form).effectiveDate"
                    defaultTime="00:00"
                  ></DateTimePickerField>
                </v-col>
                <v-col cols="6">
                  <DateTimePickerField
                    v-model="form.expirationDate"
                    :disabled="!isEditing"
                    :label="$i18n.translate('Expiration Date')"
                    :rules="rules.getDateRules(form).expirationDate"
                    defaultTime="23:59"
                  ></DateTimePickerField>
                </v-col>
              </v-row>
            </v-container>
          </v-tab-item>

          <v-tab-item>
            <v-container>
              <v-row>
                <v-col cols="12">
                  <h3 class="mb-2">{{ $i18n.translate("Claims") }}</h3>
                  <div v-if="claims.length == 0" class="text-subtitle-1 py-4 grey--text">
                    {{ $i18n.translate("No claims found for this Serial Number.") }}
                  </div>
                  <v-simple-table v-else dense class="elevation-1">
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-left">{{ $i18n.translate("CLM#") }}</th>
                          <th class="text-left">{{ $i18n.translate("Participant") }}</th>
                          <th class="text-left">{{ $i18n.translate("Promotion") }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="claim in claims" :key="claim.id">
                          <td>
                            <router-link
                              v-if="$privilege.hasPrivilege('CLAIM_READ')"
                              :to="{ name: 'claim', params: { id: claim.id } }"
                            >
                              {{ claim.claimKey }}
                            </router-link>
                            <span v-else>{{ claim.claimKey }}</span>
                          </td>
                          <td>
                            <router-link
                              v-if="claim.participant && $privilege.hasPrivilege('PARTICIPANT_READ')"
                              :to="{ name: 'participant', params: { id: claim.participant.id } }"
                            >
                              {{ claim.participant.participantKey + " - " + claim.participant.fullName }}
                            </router-link>
                            <span v-else-if="claim.participant">
                              {{ claim.participant.participantKey + " - " + claim.participant.fullName }}
                            </span>
                          </td>
                          <td>
                            <router-link
                              v-if="claim.promotion && $privilege.hasPrivilege('PROMOTION_READ')"
                              :to="{ name: 'promotion', params: { id: claim.promotion.id } }"
                            >
                              {{ claim.promotion.promotionKey }}
                            </router-link>
                            <span v-else-if="claim.promotion">{{ claim.promotion.promotionKey }}</span>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-col>
              </v-row>
            </v-container>
          </v-tab-item>

          <v-tab-item>
            <v-container>
              <v-row>
                <v-col cols="6">
                  <CustomField
                    v-for="(customField, i) in customFields"
                    :key="customField.name"
                    :customField="customField"
                    :value="form['customFieldValue' + (i + 1)]"
                  />
                </v-col>
                <v-col cols="6">
                  <h2 class="mb-2">{{ $i18n.translate("Serial Number") }} {{ $i18n.translate("Custom Fields") }}</h2>
                  <ol>
                    <li>
                      Configure each custom field at the Program Group level.
                    </li>
                  </ol>
                </v-col>
              </v-row>
            </v-container>
          </v-tab-item>

          <v-tab-item>
            <ActivityTimeline type="productSerialNumber" :id="form.id" />
          </v-tab-item>
        </v-tabs-items>
      </v-form>

      <v-dialog v-model="reactivateDialog" max-width="600px" persistent>
        <v-form v-model="reactivateFormValid" @submit.prevent="onReactivate">
          <v-card>
            <v-card-title>
              <span class="headline">{{ $i18n.translate("Reactivate Serial Number?") }}</span>
            </v-card-title>
            <v-card-text>
              <ApiError :errors="reactivateErrors" />
              <p>
                {{ $i18n.translate("The Serial Number") }} <b>{{ form.productSerialNumberKey }}</b>
                {{ $i18n.translate("will be reactivated and immediately available to be linked to a profile.") }}
              </p>
              <v-textarea
                class="required"
                :label="$i18n.translate('Reason')"
                v-model="reactivateReason"
                :rules="rules.reactivateReason"
                counter="255"
                :error-messages="$error.getValidationError(reactivateErrors, 'reason')"
                @input="$error.clearValidationError(reactivateErrors, 'reason')"
              ></v-textarea>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn @click="closeReactivateDialog">{{ $i18n.translate("Cancel") }}</v-btn>
              <v-btn
                color="primary"
                @click="onReactivate"
                :loading="reactivateLoading"
                :disabled="!reactivateFormValid"
              >
                {{ $i18n.translate("Reactivate") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-form>
      </v-dialog>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";
import ApiError from "@/gapp-components/components/display/ApiError.vue";
import CustomField from "@/gapp-components/components/fields/CustomField.vue";
import DateTimePickerField from "@/gapp-components/components/fields/DateTimePickerField.vue";
import ActivityTimeline from "@/gapp-components/components/tables/ActivityTimeline.vue";
import ValidationService from "@/gapp-components/services/validation.service.js";

export default {
  components: { ApiError, CustomField, DateTimePickerField, ActivityTimeline },
  name: "ProductSerialNumber",
  data() {
    return {
      tab: null,
      valid: true,
      isEditing: false,
      isBusy: false,
      form: {},
      errors: {},
      product: null,
      claims: [],
      reactivateDialog: false,
      reactivateFormValid: false,
      reactivateLoading: false,
      reactivateErrors: {},
      reactivateReason: ""
    };
  },
  computed: {
    ...mapGetters(["selectedClient", "selectedProgram", "selectedParticipant"]),

    canUpdate() {
      return this.$privilege.isGappInternalUser() && this.$privilege.hasPrivilege("PRODUCT_SERIAL_NUMBER_UPDATE");
    },

    customFields() {
      return this.selectedProgram.programGroup.productSerialNumberCustomFields;
    },

    rules() {
      return {
        productSerialNumberKey: [
          ValidationService.generateNotWhitespacesRule(this.$i18n.translate("Serial Number")),
          ...ValidationService.generateTextValidationRules(this.$i18n.translate("Serial Number"), 100)
        ],
        reactivateReason: [
          ValidationService.generateNotWhitespacesRule(this.$i18n.translate("Reason")),
          ...ValidationService.generateTextValidationRules(this.$i18n.translate("Reason"), 255)
        ],
        getDateRules: form => {
          const dateRules = ValidationService.generateDateRangeValidationRules(
            this.$i18n.translate("Effective Date"),
            this.$i18n.translate("Expiration Date"),
            () => form.effectiveDate,
            () => form.expirationDate,
            false
          );

          return {
            effectiveDate: [dateRules[0]],
            expirationDate: [dateRules[1]]
          };
        }
      };
    }
  },
  methods: {
    fetchData() {
      this.isBusy = true;
      this.$api
        .get("/api/productSerialNumbers/" + this.$route.params.id)
        .then(({ data }) => {
          this.form = data;
          this.$emit("loaded", data);

          return Promise.all([
            this.$api
              .getRelatedObject("product", data)
              .then(({ data }) => {
                this.product = data;
              })
              .catch(() => {
                console.log("No product found");
              }),
            this.$api
              .post("/api/claims/search", { serialNumber: this.form.productSerialNumberKey })
              .then(({ data }) => {
                this.claims = data.content;
              })
          ]);
        })
        .catch(error => {
          this.errors = this.$api.getErrorsFromResponse(error);
        })
        .finally(() => {
          this.isBusy = false;
        });
    },
    onEdit() {
      this.isEditing = !this.isEditing;
      if (!this.isEditing) {
        this.fetchData();
      }
    },
    onSubmit() {
      this.isBusy = true;
      this.errors = {};
      this.$api
        .patch("/api/productSerialNumbers/" + this.$route.params.id, this.form)
        .then(({ data }) => {
          this.form = data;
          this.isEditing = false;
        })
        .catch(error => {
          this.isEditing = true;
          this.errors = this.$api.getErrorsFromResponse(error);
        })
        .finally(() => {
          this.isBusy = false;
        });
    },
    onReactivate() {
      this.isBusy = true;
      this.reactivateErrors = {};
      this.form.reason = this.reactivateReason.trim();
      this.$api
        .post(`/api/productSerialNumbers/${this.$route.params.id}/activate`, this.form)
        .then(() => {
          this.closeReactivateDialog();
          this.fetchData();
        })
        .catch(error => {
          this.reactivateErrors = this.$api.getErrorsFromResponse(error);
        })
        .finally(() => {
          this.isBusy = false;
        });
    },
    closeReactivateDialog() {
      this.reactivateDialog = false;
      this.reactivateReason = "";
      this.reactivateErrors = {};
    }
  },
  mounted() {
    this.fetchData();
  }
};
</script>
